/**
 * Userpilot Public API
 *
 * This module provides the public API for Userpilot integration:
 * - Type-safe event constants (TRACK_EVENTS)
 * - Survey codes (SURVEY_CODES)
 *
 * ## Usage
 *
 * ### Initialization (App.tsx only)
 * ```typescript
 * import { useUserpilotInit } from '../hooks/useUserpilotInit';
 *
 * const AppContent = () => {
 *   useUserpilotInit(); // Initialize once at app root
 *   // ... rest of app
 * };
 * ```
 *
 * ### Tracking (any component)
 * ```typescript
 * import { useUserpilotTracking } from '../hooks/useUserpilotTracking';
 * import { TRACK_EVENTS, SURVEY_CODES } from '../userpilot';
 *
 * const MyComponent = () => {
 *   const { trackEvent, triggerSurvey } = useUserpilotTracking();
 *
 *   trackEvent(TRACK_EVENTS.RECORDING_STARTED, {
 *     sessionId: '123',
 *     deviceId: 'default',
 *     deviceLabel: 'Built-in Microphone'
 *   });
 *
 *   triggerSurvey(SURVEY_CODES.POST_RECORDING_FEEDBACK);
 * };
 * ```
 */

/**
 * Brands for which Userpilot is enabled
 * Update this array to enable/disable Userpilot for specific brands
 */
export const ENABLED_BRANDS = ['ct-classic'] as const;

import {
  createEvent,
  type DocumentActionParams,
  type DocumentGenerationParams,
  type ExistingSessionViewedParams,
  type NoteFeedbackParams,
  type RecordingEventParams,
  type RecordingRestartedParams,
  type TemplateSelectionParams,
} from './internal/types';

/**
 * Tracked events for Userpilot
 * Each event carries its own parameter type information for type-safe tracking
 */
export const TRACK_EVENTS = {
  RECORDING_STARTED: createEvent<RecordingEventParams>('recording_started'),
  RECORDING_PAUSED: createEvent<RecordingEventParams>('recording_paused'),
  RECORDING_RESUMED: createEvent<RecordingEventParams>('recording_resumed'),
  RECORDING_CONFLICT: createEvent<RecordingEventParams>('recording_conflict'),
  RECORDING_RESUME_CONFIRMATION_SHOWN: createEvent<RecordingEventParams>(
    'recording_resume_confirmation_shown'
  ),
  RECORDING_RESUME_CONFIRMATION_CONFIRMED: createEvent<RecordingEventParams>(
    'recording_resume_confirmation_confirmed'
  ),
  RECORDING_RESUME_CONFIRMATION_CANCELLED: createEvent<RecordingEventParams>(
    'recording_resume_confirmation_cancelled'
  ),
  RECORDING_STOPPED: createEvent<RecordingEventParams>('recording_stopped'),
  RECORDING_COMPLETED: createEvent<DocumentGenerationParams>(
    'recording_completed'
  ),
  RECORDING_RESTARTED: createEvent<RecordingRestartedParams>(
    'recording_restarted'
  ),
  RECORDING_AUDIO_DROPPED: createEvent<RecordingEventParams>(
    'recording_audio_dropped'
  ),
  RECORDING_PAUSE_CHECKPOINT_FAILED: createEvent<RecordingEventParams>(
    'recording_pause_checkpoint_failed'
  ),

  DOCUMENT_EXPORTED: createEvent<DocumentActionParams>('document_exported'),
  DOCUMENT_EDITED: createEvent<DocumentActionParams>('document_edited'),

  TEMPLATE_SELECTED: createEvent<TemplateSelectionParams>('template_selected'),

  EXISTING_SESSION_VIEWED: createEvent<ExistingSessionViewedParams>(
    'existing_session_viewed'
  ),

  NOTE_FEEDBACK_RATING_SELECTED: createEvent<NoteFeedbackParams>(
    'note_feedback_rating_selected'
  ),
  NOTE_FEEDBACK_RATING_CHANGED: createEvent<NoteFeedbackParams>(
    'note_feedback_rating_changed'
  ),
  NOTE_FEEDBACK_PANEL_OPENED: createEvent<NoteFeedbackParams>(
    'note_feedback_panel_opened'
  ),
  NOTE_FEEDBACK_ENRICHMENT_SUBMITTED: createEvent<NoteFeedbackParams>(
    'note_feedback_enrichment_submitted'
  ),
  NOTE_FEEDBACK_DISMISSED: createEvent<NoteFeedbackParams>(
    'note_feedback_dismissed'
  ),
  NOTE_FEEDBACK_CONFIRMATION_SHOWN: createEvent<NoteFeedbackParams>(
    'note_feedback_confirmation_shown'
  ),
} as const;

/**
 * Survey codes for triggering Userpilot surveys
 */
export const SURVEY_CODES = {
  POST_RECORDING_FEEDBACK: 'post_recording_feedback',
} as const;
